import { useContext, useEffect, useMemo, useState } from "react";
import { Loader } from "../../../../../../common/components/Loader/Loader";
import { TimesheetOperation } from "../../../../core/_request";
import { HttpStatusCode } from "axios";
import style from "./style.module.scss";
import { DisplayTable } from "../../../../../../common/components/LFTable/DisplayTable";
import { TableTypes } from "../../../../../../common/components/LFTable/Table.model";
import { OperationContext } from "../../CreateOperation";
import { TitleBadge } from "../../components/TitleBadge";
import { RenderMovement } from "../../components/RenderMovement";

interface TimesheetTableProps {
  team: {
    id: number;
    name: string;
  };

  onDateAvailabilityChange?: (hasData: boolean) => void;
}

export const TimesheetTable = ({ team, onDateAvailabilityChange }: TimesheetTableProps) => {
  const { initialValue, filter } = useContext(OperationContext);
  const id = initialValue.id;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState<any>();

  const flattenedData = useMemo(() => {
    if (!tableData?.value) {
      return [];
    }

    return tableData.value.flatMap((period: any, periodIndex: number) => {
      let debarkationMovements = period?.value?.debarkationMovement ?? [];

      let embarkationMovements = period?.value?.embarkationMovement ?? [];

      const bothSidesEmpty =
        debarkationMovements.length === 0 && embarkationMovements.length === 0;

      /*
       * Lorsqu'il n'y a absolument aucun mouvement,
       * on fabrique la ligne :
       *
       * 15:00 | 20:00 | X | 0 T / 0 M3
       */
      if (bothSidesEmpty) {
        const emptyMovement = {
          startAt: period.periodStart,
          endAt: period.periodEnd,

          nature: {
            label: "X",
            detail: [],
          },

          tonnageVolumeLabel: "0 T / 0 M3",

          isSynthetic: true,
        };

        debarkationMovements = [emptyMovement];
        embarkationMovements = [emptyMovement];
      }

      const rowCount = Math.max(
        debarkationMovements.length,
        embarkationMovements.length,
        1
      );

      return Array.from({ length: rowCount }, (_, rowIndex) => ({
        key: `${periodIndex}-${rowIndex}`,

        debarkationMovement: debarkationMovements[rowIndex] ?? null,

        embarkationMovement: embarkationMovements[rowIndex] ?? null,

        rateLabel: period.rateLabel,

        /*
         * Majoration doit occuper toutes les lignes
         * appartenant à cette tranche horaire.
         */
        rateRowSpan: rowIndex === 0 ? rowCount : 0,

        /*
         * Permet de savoir où se termine une majoration.
         */
        isPeriodEnd: rowIndex === rowCount - 1,

        /*
         * Toutes les autres lignes sont internes à
         * la même majoration.
         */
        isPeriodInner: rowIndex < rowCount - 1,
      }));
    });
  }, [tableData]);

  const displayData = useMemo(
    () => ({
      ...tableData,
      value: flattenedData,
    }),
    [tableData, flattenedData]
  );

  const getMovementCell = (
    side: "debarkationMovement" | "embarkationMovement"
  ) => {
    return (record: any) => ({
      className: record?.[side]?.auxiliary ? "timesheet-auxiliary-cell" : "",
    });
  };

  const columns: TableTypes = [
    /*
     * ==========================
     * DÉBARQUEMENT
     * ==========================
     */
    {
      title: "DÉBARQUEMENT",
      children: [
        {
          title: "Début",
          dataIndex: "debarkationMovement",
          width: "8%",

          onCell: getMovementCell("debarkationMovement"),

          render: (movement: any) => (
            <RenderMovement movement={movement} field="start" />
          ),
        },

        {
          title: "Fin",
          dataIndex: "debarkationMovement",
          width: "8%",
          onCell: getMovementCell("debarkationMovement"),

          render: (movement: any) => (
            <RenderMovement movement={movement} field="end" />
          ),
        },

        {
          title: "Nature",
          dataIndex: "debarkationMovement",
          width: "18%",
          onCell: getMovementCell("debarkationMovement"),

          render: (movement: any) => (
            <RenderMovement movement={movement} field="nature" />
          ),
        },

        {
          title: "T / M3",
          dataIndex: "debarkationMovement",
          width: "11%",
          onCell: getMovementCell("debarkationMovement"),

          render: (movement: any) => (
            <RenderMovement movement={movement} field="tonnage" />
          ),
        },
      ],
    },

    /*
     * ==========================
     * EMBARQUEMENT
     * ==========================
     */
    {
      title: "EMBARQUEMENT",
      children: [
        {
          title: "Début",
          dataIndex: "embarkationMovement",
          width: "8%",

          onCell: getMovementCell("embarkationMovement"),

          render: (movement: any) => (
            <RenderMovement movement={movement} field="start" />
          ),
        },

        {
          title: "Fin",
          dataIndex: "embarkationMovement",
          width: "8%",
          onCell: getMovementCell("embarkationMovement"),

          render: (movement: any) => (
            <RenderMovement movement={movement} field="end" />
          ),
        },

        {
          title: "Nature",
          dataIndex: "embarkationMovement",
          width: "18%",
          onCell: getMovementCell("embarkationMovement"),

          render: (movement: any) => (
            <RenderMovement movement={movement} field="nature" />
          ),
        },

        {
          title: "T / M3",
          dataIndex: "embarkationMovement",
          width: "11%",
          onCell: getMovementCell("embarkationMovement"),

          render: (movement: any) => (
            <RenderMovement movement={movement} field="tonnage" />
          ),
        },
      ],
    },

    /*
     * ==========================
     * MAJORATION
     * ==========================
     */
    {
      title: "Majoration",
      dataIndex: "rateLabel",
      width: "10%",

      onCell: (record: any) => ({
        rowSpan: record.rateRowSpan,
      }),

      render: (rateLabel: string) => (
        <div className={style.table__cell__date}>{rateLabel ?? "--"}</div>
      ),
    },
  ];

  useEffect(() => {
  if (!team?.id || !filter?.teamsCreatedAt) return;

  getTimesheetData(id, team.id, {
    dates: [filter.teamsCreatedAt],
  });
}, [team?.id, filter?.teamsCreatedAt]);

  const getTimesheetData = async (id: number, teamId: number, params?: any) => {
    try {
      setIsLoading(true);
      const res = await TimesheetOperation.getAllTimesheet(id, teamId, params);
      if (res?.status === HttpStatusCode.Ok) {
        setTableData(res?.data);

        onDateAvailabilityChange?.(res?.data?.date != null); // if data: null → onDateAvailabilityChange?.(false)
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Loader isLoading={isLoading} />
      <div className="my-2" style={{ overflowX: "auto", width: "100%" }}>
        <div className="">
          <div className={style.table__team}>
            <span style={{ color: "#383938" }}>
              <strong>Membres de l'équipe</strong>
            </span>
            {tableData?.allStaffs?.map((item: any) => (
              <div className="d-flex gap-2">
                <span> {item?.post} : </span>
                <span
                  style={{
                    fontWeight: "bold",
                  }}
                >
                  {" "}
                  {item?.names}{" "}
                </span>
              </div>
            ))}
          </div>
          {tableData?.recapPerHold?.length > 0 && (
            <div className={style.table__recap_container}>
              <p className={style.table__recap_title}>
                <strong>Récapitulatif par cale :</strong>
              </p>

              <div className={style.table__recap_badges}>
                {tableData?.recapPerHold?.map((hold: any) => (
                  <TitleBadge
                    key={hold.holdNumber}
                    label={`Cale ${hold.holdNumber}`}
                    value={`${hold.totalCount}/${hold.totalTonnage} T`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className={style.table__timesheet}>
          <DisplayTable
            data={displayData}
            columns={columns}
            timesheetSummary
            rowClassName={(record: any) =>
              record.isPeriodEnd
                ? "timesheet-period-end"
                : "timesheet-period-inner"
            }
          />
        </div>
      </div>
    </div>
  );
};
